import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Card,
  CardContent,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import {
  Delete,
  VideoLibrary,
  Audiotrack,
  Link as LinkIcon,
  Add,
} from '@mui/icons-material';
import { BandRecommendationFormData } from '../../types/BandRecommendations';
import AttentionAddButton from './AttentionAddButton';

interface MediaLinksProps {
  formData: BandRecommendationFormData;
  setFormData: React.Dispatch<React.SetStateAction<BandRecommendationFormData>>;
}

// ─── Embed helpers ────────────────────────────────────────────────────────────

const getYouTubeEmbed = (url: string): string | null => {
  const match = url.match(/^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
  if (match && match[2].length === 11) {
    return `<iframe width="560" height="315" src="https://www.youtube.com/embed/${match[2]}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
  }
  return null;
};

const getSpotifyEmbed = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const [, contentType, contentId] = parsed.pathname.split('/');
    if (contentType && contentId) {
      const height = contentType === 'track' || contentType === 'episode' ? '80' : '380';
      return `<iframe src="https://open.spotify.com/embed/${contentType}/${contentId}" width="100%" height="${height}" frameborder="0" allowtransparency="true" allow="encrypted-media"></iframe>`;
    }
  } catch { /* invalid url */ }
  return null;
};

const getSoundCloudEmbed = async (url: string): Promise<string | null> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(
      `https://soundcloud.com/oembed?format=json&url=${encodeURIComponent(url)}`,
      { signal: controller.signal }
    );
    clearTimeout(timeout);
    if (!res.ok) return null;
    const data = await res.json();
    return data.html || null;
  } catch {
    clearTimeout(timeout);
    return null;
  }
};

// Bandcamp: accept either a raw <iframe> embed code or a URL saved as a link
const getBandcampEmbed = (input: string): string | null => {
  const trimmed = input.trim();
  if (trimmed.toLowerCase().startsWith('<iframe')) return trimmed;
  if (/^https?:\/\/.+\.bandcamp\.com/i.test(trimmed)) return trimmed;
  return null;
};

const isBandcampEmbedCode = (s: string) => s.trim().toLowerCase().startsWith('<iframe');

// ─── Inline embed renderer ────────────────────────────────────────────────────

const InlineEmbed: React.FC<{ html: string; platform: string }> = ({ html, platform }) => {
  if (platform === 'bandcamp' && !isBandcampEmbedCode(html)) {
    return (
      <Box sx={{ py: 1 }}>
        <a href={html} target="_blank" rel="noopener noreferrer" style={{ color: '#3A9188', wordBreak: 'break-all' }}>
          {html}
        </a>
        {html.toLowerCase().endsWith('.mp3') && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            MP3 file — audio player coming soon
          </Typography>
        )}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        mt: 1,
        borderRadius: 1,
        overflow: 'hidden',
        '& iframe': { display: 'block', maxWidth: '100%' },
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const MediaLinks: React.FC<MediaLinksProps> = ({ formData, setFormData }) => {
  const [inputs, setInputs] = useState({ youtube: '', spotify: '', soundcloud: '', bandcamp: '' });
  const [errors, setErrors]   = useState({ youtube: '', spotify: '', soundcloud: '', bandcamp: '' });
  const [loading, setLoading] = useState({ youtube: false, spotify: false, soundcloud: false, bandcamp: false });

  // "Other links" state
  const [otherUrl,   setOtherUrl]   = useState('');
  const [otherLabel, setOtherLabel] = useState('');
  const [otherError, setOtherError] = useState('');

  const validate = (platform: string, input: string): string => {
    if (!input.trim()) return 'Please enter a URL';
    switch (platform) {
      case 'youtube':
        return /youtu(\.be|be\.com)\/.+/i.test(input) ? '' : 'Please enter a valid YouTube URL';
      case 'spotify':
        return /open\.spotify\.com\/(track|album|playlist|artist|episode)\//i.test(input)
          ? '' : 'Please enter a valid Spotify URL';
      case 'soundcloud':
        return /soundcloud\.com\/.+/i.test(input) ? '' : 'Please enter a valid SoundCloud URL';
      case 'bandcamp':
        return /bandcamp\.com/.test(input) || input.trim().toLowerCase().startsWith('<iframe')
          ? '' : 'Paste a Bandcamp URL (artist.bandcamp.com/...) or your Bandcamp embed code';
      default: return '';
    }
  };

  const handleAdd = (platform: keyof typeof inputs) => async () => {
    const input = inputs[platform].trim();
    const error = validate(platform, input);
    if (error) { setErrors(p => ({ ...p, [platform]: error })); return; }

    setLoading(p => ({ ...p, [platform]: true }));
    setErrors(p => ({ ...p, [platform]: '' }));

    try {
      let embedCode: string | null = null;
      if (platform === 'youtube')    embedCode = getYouTubeEmbed(input);
      if (platform === 'spotify')    embedCode = getSpotifyEmbed(input);
      if (platform === 'soundcloud') embedCode = await getSoundCloudEmbed(input);
      if (platform === 'bandcamp')   embedCode = getBandcampEmbed(input);

      if (!embedCode) {
        setErrors(p => ({ ...p, [platform]: 'Could not process this URL. Please check and try again.' }));
        return;
      }

      const key = `${platform}Embeds` as keyof BandRecommendationFormData;
      const existing = formData[key] as string[];
      if (existing.includes(embedCode)) {
        setErrors(p => ({ ...p, [platform]: 'Already added.' }));
        return;
      }

      setFormData(p => ({ ...p, [key]: [...existing, embedCode!] }));
      setInputs(p => ({ ...p, [platform]: '' }));
    } catch {
      setErrors(p => ({ ...p, [platform]: 'Error processing URL. Please try again.' }));
    } finally {
      setLoading(p => ({ ...p, [platform]: false }));
    }
  };

  const handleRemove = (platform: keyof typeof inputs, index: number) => () => {
    const key = `${platform}Embeds` as keyof BandRecommendationFormData;
    setFormData(p => ({
      ...p,
      [key]: (p[key] as string[]).filter((_, i) => i !== index),
    }));
  };

  const handleAddOtherLink = () => {
    if (!otherUrl.trim()) { setOtherError('Please enter a URL'); return; }
    try { new URL(otherUrl.trim()); } catch { setOtherError('Please enter a valid URL'); return; }
    setOtherError('');
    const newLink = { url: otherUrl.trim(), label: otherLabel.trim() };
    setFormData(p => ({ ...p, musicLinks: [...(p.musicLinks || []), newLink] }));
    setOtherUrl('');
    setOtherLabel('');
  };

  const handleRemoveOtherLink = (index: number) => {
    setFormData(p => ({ ...p, musicLinks: (p.musicLinks || []).filter((_, i) => i !== index) }));
  };

  const renderPlatform = (
    platform: keyof typeof inputs,
    label: string,
    placeholder: string,
    hint?: string,
  ) => {
    const embeds = formData[`${platform}Embeds` as keyof BandRecommendationFormData] as string[];
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {label}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {hint}
          </Typography>
        )}

        <Grid container spacing={1} alignItems="flex-start">
          <Grid item xs={12} sm={8}>
            <TextField
              value={inputs[platform]}
              onChange={(e) => {
                setInputs(p => ({ ...p, [platform]: e.target.value }));
                if (errors[platform]) setErrors(p => ({ ...p, [platform]: '' }));
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(platform)(); } }}
              variant="outlined"
              fullWidth
              size="small"
              placeholder={placeholder}
              error={!!errors[platform]}
              helperText={errors[platform]}
              inputProps={{ autoComplete: 'off' }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <AttentionAddButton
              hasInput={Boolean(inputs[platform])}
              onClick={handleAdd(platform)}
              loading={loading[platform]}
              disabled={loading[platform]}
              fullWidth
            />
          </Grid>
        </Grid>

        {/* Inline embeds — no modal, no eye icon */}
        {embeds?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {embeds.map((embed, i) => (
              <Box
                key={i}
                sx={{
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <InlineEmbed html={embed} platform={platform} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 0.5 }}>
                  <IconButton size="small" color="error" onClick={handleRemove(platform, i)} aria-label="Remove">
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Card>
        <CardContent>
          {/* ── Explainer ─────────────────────────────────────── */}
          <Box
            sx={{
              mb: 4,
              p: 2,
              borderRadius: 1,
              bgcolor: 'rgba(58, 145, 136, 0.1)',
              borderLeft: '3px solid #3A9188',
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Make your page stand out
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Artists with embedded audio and video get significantly more engagement. Rather than just linking out,
              embedded players let people listen and watch right on your page without leaving.
              Paste a YouTube or SoundCloud URL and we'll handle the rest. For Spotify and Bandcamp,
              grab the embed code from their share options — it takes seconds and gives you a full player on your profile.
            </Typography>
          </Box>

          {/* ── VIDEO ─────────────────────────────────────────── */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <VideoLibrary color="primary" />
            Video
          </Typography>

          {renderPlatform('youtube', 'YouTube', 'Paste a YouTube URL and press Enter or Add')}

          <Divider sx={{ my: 3 }} />

          {/* ── AUDIO ─────────────────────────────────────────── */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Audiotrack color="primary" />
            Audio
          </Typography>

          {renderPlatform('spotify', 'Spotify', 'Paste a Spotify track, album or playlist URL')}
          {renderPlatform('soundcloud', 'SoundCloud', 'Paste a SoundCloud track or playlist URL')}
          {renderPlatform(
            'bandcamp',
            'Bandcamp',
            'Paste your Bandcamp URL or the embed code from your Bandcamp track/album page',
            'As the artist you can copy the embed code from your Bandcamp release page (Share → Embed). Paste it directly here for a full player, or just paste your Bandcamp URL.',
          )}

          <Divider sx={{ my: 3 }} />

          {/* ── OTHER LINKS ───────────────────────────────────── */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LinkIcon color="primary" />
            Other Music Links
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Link to music on your own website, Google Drive, or anywhere else it lives.
            If the link ends in .mp3 it will get an audio player when that feature is ready.
          </Typography>

          <Grid container spacing={1} alignItems="flex-start">
            <Grid item xs={12} sm={5}>
              <TextField
                value={otherUrl}
                onChange={(e) => { setOtherUrl(e.target.value); if (otherError) setOtherError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOtherLink(); } }}
                variant="outlined"
                fullWidth
                size="small"
                placeholder="https://…"
                label="URL"
                error={!!otherError}
                helperText={otherError}
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                value={otherLabel}
                onChange={(e) => setOtherLabel(e.target.value)}
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Label (optional)"
                label="Label"
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AttentionAddButton
                hasInput={Boolean(otherUrl)}
                onClick={handleAddOtherLink}
                loading={false}
                disabled={false}
                fullWidth
              />
            </Grid>
          </Grid>

          {(formData.musicLinks || []).length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(formData.musicLinks || []).map((link, i) => (
                <Chip
                  key={i}
                  label={link.label || link.url}
                  component="a"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  onDelete={() => handleRemoveOtherLink(i)}
                  size="small"
                  color={link.url.toLowerCase().endsWith('.mp3') ? 'warning' : 'default'}
                  title={link.url}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MediaLinks;
